import {useEffect, useSyncExternalStore} from 'react';
import {z} from 'zod';
import definition from '../../config/desktop-release.json';
import {platform} from '../platform/transport';
import {errorDetails} from './store';
import type {ErrorDetails} from './types';

type DesktopPlatform = keyof typeof definition.platforms;
const platforms = Object.keys(definition.platforms) as [DesktopPlatform, ...DesktopPlatform[]];
const releaseSchema = z.object({
  schema: z.literal(definition.schema),
  version: z.string().regex(new RegExp(definition.versionPattern)),
  commit: z.string().regex(/^[0-9a-f]{40}$/),
  publishedAt: z.iso.datetime(),
  downloads: z.array(z.object({
    platform: z.enum(platforms), url: z.url(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/), bytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  }).strict()).length(platforms.length),
}).strict();

type DesktopRelease = z.infer<typeof releaseSchema>;
type ReleaseState = {status: 'loading' | 'unpublished'} |
  {status: 'available'; release: DesktopRelease} | {status: 'error'; error: ErrorDetails};
let state: ReleaseState = {status: 'loading'};
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();

function update(next: ReleaseState) {
  state = next;
  for (const listener of listeners) listener();
}

function readRelease(): Promise<void> {
  if (pending) return pending;
  update({status: 'loading'});
  pending = (async () => {
    const root = new URL(`${import.meta.env.BASE_URL}${definition.path.slice(1)}/`, platform().publicOrigin);
    if (root.protocol !== 'https:' || root.username || root.password || root.search || root.hash) {
      throw new Error('Desktop downloads require the EightBots HTTPS origin.');
    }
    const response = await fetch(new URL('release.json', root), {cache: 'no-store', credentials: 'omit', redirect: 'error'});
    if (response.status === 404) {update({status: 'unpublished'}); return;}
    const body = await response.text();
    if (!response.ok) throw new Error(`Desktop release read failed: HTTP ${response.status} ${response.statusText}\n${body}`);
    if (response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase() !== 'application/json') {
      throw new Error(`Desktop release returned ${response.headers.get('content-type') ?? 'no content type'}, expected application/json.\n${body}`);
    }
    let value: unknown;
    try {value = JSON.parse(body);} catch (cause) {throw new Error(`Desktop release contains invalid JSON.\n${body}`, {cause});}
    const release = releaseSchema.parse(value);
    const seen = new Set<DesktopPlatform>();
    for (const download of release.downloads) {
      if (seen.has(download.platform)) throw new Error(`Desktop release repeats platform ${download.platform}.`);
      seen.add(download.platform);
      const extension = definition.platforms[download.platform].installerExtension;
      const expected = new URL(`${release.version}/EightBots-${release.version}-${download.platform}${extension}`, root);
      if (download.url !== expected.href) {
        throw new Error(`Invalid desktop installer URL for ${download.platform}: ${download.url}. Expected ${expected.href}.`);
      }
    }
    const downloads = platforms.map(value => release.downloads.find(download => download.platform === value)!);
    update({status: 'available', release: {...release, downloads}});
  })().catch(failure => update({status: 'error', error: errorDetails(failure)})).finally(() => {pending = null;});
  return pending;
}

function refreshVisible() {if (document.visibilityState === 'visible') void readRelease();}
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) document.addEventListener('visibilitychange', refreshVisible);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) document.removeEventListener('visibilitychange', refreshVisible);
  };
}

export function useDesktopRelease() {
  const release = useSyncExternalStore(subscribe, () => state);
  useEffect(() => {void readRelease();}, []);
  return release;
}

export function desktopPlatformLabel(value: DesktopPlatform): string {return definition.platforms[value].label;}
